import { ApolloClient, ApolloLink, InMemoryCache, split } from '@apollo/client'
import { createUploadLink } from 'apollo-upload-client'
import { setContext } from 'apollo-link-context'
import { onError } from '@apollo/client/link/error'
import { WebSocketLink } from '@apollo/client/link/ws'
import { getMainDefinition, Observable } from '@apollo/client/utilities'
import { toastSweetAlert } from '../Components/Helpers/ToastSweetAlert'
import { waitForServerViaWS } from './WaitForServerViaWS'

// Configuración del HTTP link para GraphQL
const httpLink = createUploadLink({
  uri: process.env.REACT_APP_GRAPH_SERVER, // URL de tu servidor GraphQL
})

// Configuración de la autenticación
const authLink = setContext((_, { headers }) => {
  const token = localStorage.getItem('token')
  const requestPath = window.location.pathname
  // Si el token no existe, no se envía en la cabecera
  if (!token) {
    return {
      headers: {
        ...headers,
        requestPath,
        // 'x-client-source': 'web',
      },
    }
  }
  // Si el token existe, se envía en la cabecera
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
      requestPath,
      // 'x-client-source': 'web',
    },
  }
})

// Manejo de errores de Apollo Client
const errorLink = onError(({ graphQLErrors, networkError }) => {
  // Si hay un error de red
  if (networkError) {
    //Server no disponible o inalcanzable
    if (networkError.message === 'Failed to fetch') {
      // Iniciar el intento de reconexión
      waitForServerViaWS()

      return new Observable(() => { })
    }
  }
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, extensions }) => {
      // Revisar si el error está relacionado con un token no válido
      if (
        extensions.code === 'UNAUTHENTICATED' ||
        message.includes('Invalid token')
      ) {
        console.log('Token inválido o expirado')
        localStorage.removeItem('token') // Eliminar el token
        window.location.replace(`/login?reason=expired`) // Redirigir al login
      }
    })
  }
})

// Este link detiene la petición si no hay internet
const offlineLink = new ApolloLink((operation, forward) => {
  if (!navigator.onLine) {
    // Mostrar mensaje de advertencia al usuario
    toastSweetAlert({
      mode: 'modalGeneric',
      title: 'Sin conexión',
      message:
        'No tienes conexión a internet. Intenta nuevamente cuando estés conectado.',
      icon: 'warning',
    })
    // No continuar con la petición
    return new Observable(() => { })
  }

  // Si hay internet, continuar con la cadena de links
  return forward(operation)
})

// Configuración del WebSocket para suscripciones
const wsLink = new WebSocketLink({
  uri: process.env.REACT_APP_GRAPH_WS_SERVER,
  options: {
    reconnect: true,
  },
})

// Dividir el tráfico entre las suscripciones (WebSocket) y las consultas/mutaciones (HTTP)
const link = split(
  ({ query }) => {
    const definition = getMainDefinition(query)
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    )
  },
  wsLink,
  ApolloLink.from([
    authLink,
    offlineLink, // Aquí intercepta si no hay internet
    errorLink,
    httpLink,
  ])
)

const client = new ApolloClient({
  connectToDevTools: true,
  cache: new InMemoryCache({ addTypename: false }),
  link,
})

export default client
